import { Button, Collapse, DatePicker, Form, FormInstance, Radio, Select, Space } from 'antd';
import React, { useState, useCallback, useEffect } from 'react';
import { CompanyInterface } from 'type/companyType';
import { SeasonInterface } from 'type/seasonType';
import { companyApiService } from 'service/api/companyApiService';
import { seasonService } from 'content/service/seasonService';
import { debounce } from 'lodash';

const { Option } = Select;

type RevenueFilterProps = {
  form: FormInstance;
  searchParams: URLSearchParams;
  handleFilterValueChange: (
    value: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
  handleFilterSubmit: () => void;
  handleFilterReset: () => void;
};
export const RevenueFilter: React.FC<RevenueFilterProps> = ({
  form,
  handleFilterValueChange,
  handleFilterSubmit,
  handleFilterReset,
}: RevenueFilterProps) => {
  const [searchCompanies, setSearchCompanies] = useState<CompanyInterface[]>([]);
  const [defaultCompanies, setDefaultCompanies] = useState<CompanyInterface[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [searchSeasons, setSearchSeasons] = useState<SeasonInterface[]>([]);
  const [defaultSeasons, setDefaultSeasons] = useState<SeasonInterface[]>([]);
  const [seasonSearchLoading, setSeasonSearchLoading] = useState(false);

  // 기본 회사 목록 로드 (100개)
  useEffect(() => {
    const loadDefaultCompanies = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        const result = await companyApiService.fetch(queryParams);

        setDefaultCompanies(result.results);
        setSearchCompanies(result.results); // 초기에는 기본 목록을 검색 결과로도 설정
      } catch (error) {
        console.error('❌ [Revenue Dashboard] Failed to load default companies:', error);
      }
    };

    loadDefaultCompanies();
  }, []);

  // 기본 시즌 목록 로드 (100개)
  useEffect(() => {
    const loadDefaultSeasons = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        const result = await seasonService.fetch(queryParams);

        setDefaultSeasons(result.results);
        setSearchSeasons(result.results); // 초기에는 기본 목록을 검색 결과로도 설정
      } catch (error) {
        console.error('❌ [Revenue Dashboard] Failed to load default seasons:', error);
      }
    };

    loadDefaultSeasons();
  }, []);

  // Season 검색 함수 (debounce 적용)
  const debouncedSearchSeasons = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // 검색어가 없으면 기본 목록 표시
        setSearchSeasons(defaultSeasons);
        return;
      }

      setSeasonSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await seasonService.fetch(queryParams);
        setSearchSeasons(result.results);
      } catch (error) {
        console.error('Season search failed:', error);
        setSearchSeasons([]);
      } finally {
        setSeasonSearchLoading(false);
      }
    }, 300),
    [defaultSeasons]
  );

  // Season Select 박스가 열릴 때 기본 목록 복원
  const handleSeasonDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Select 박스가 열릴 때마다 기본 목록 표시
      setSearchSeasons(defaultSeasons);
    }
  };

  // Season 검색어 변경 처리
  const handleSeasonSearch = (value: string) => {
    if (!value.trim()) {
      // 검색어가 비어있으면 즉시 기본 목록 표시
      setSearchSeasons(defaultSeasons);
    } else {
      debouncedSearchSeasons(value);
    }
  };

  // Season X 버튼으로 검색어 지웠을 때 처리
  const handleSeasonClear = () => {
    setSearchSeasons(defaultSeasons);
  };

  // Company 검색 함수 (debounce 적용)
  const debouncedSearchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        // 검색어가 없으면 기본 목록 표시
        setSearchCompanies(defaultCompanies);
        return;
      }

      setSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await companyApiService.fetch(queryParams);
        setSearchCompanies(result.results);
      } catch (error) {
        console.error('Company search failed:', error);
        setSearchCompanies([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [defaultCompanies]
  );

  // Select 박스가 열릴 때 기본 목록 복원
  const handleDropdownVisibleChange = (open: boolean) => {
    if (open) {
      // Select 박스가 열릴 때마다 기본 목록 표시
      setSearchCompanies(defaultCompanies);
    }
  };

  // 검색어 변경 처리
  const handleSearch = (value: string) => {
    if (!value.trim()) {
      // 검색어가 비어있으면 즉시 기본 목록 표시
      setSearchCompanies(defaultCompanies);
    } else {
      debouncedSearchCompanies(value);
    }
  };

  // X 버튼으로 검색어 지웠을 때 처리
  const handleClear = () => {
    setSearchCompanies(defaultCompanies);
  };

  // Company 옵션 렌더링 함수
  const renderCompanyOption = (company: CompanyInterface) => {
    const titlePart = company.title_en ? `${company.title} (${company.title_en})` : company.title;
    const countryPart = company.country ? ` / ${company.country}` : '';
    const label = titlePart + countryPart;

    return (
      <Option key={company.id} value={company.id.toString()} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  // Season 옵션 렌더링 함수
  const renderSeasonOption = (season: SeasonInterface) => {
    const label = season.title;

    return (
      <Option key={season.id} value={season.id.toString()} title={label}>
        <span style={{ fontWeight: 500 }}>{label}</span>
      </Option>
    );
  };

  return (
    <Collapse
      defaultActiveKey={['1']}
      style={{ marginBottom: 20 }}
      items={[
        {
          key: '1',
          label: 'Revenue Filter',
          children: (
            <Form form={form} layout="horizontal" onFinish={handleFilterSubmit}>
              <div>
                <Form.Item name="start_yearmon" label="Start Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select start date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange((prev: Record<string, string>) => ({
                        ...prev,
                        start_yearmon: e.format('YYYY-MM'),
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item name="end_yearmon" label="End Date">
                  <DatePicker
                    picker="month"
                    style={{ width: '10%' }}
                    placeholder="Select end date"
                    format="YYYY-MM"
                    onChange={e =>
                      handleFilterValueChange(prev => ({
                        ...prev,
                        end_yearmon: e ? e.format('YYYY-MM') : '',
                      }))
                    }
                  />
                </Form.Item>
              </div>
              <Form.Item
                name="content_provider_ids"
                label="Content Provider"
                style={{ width: '50%' }}
              >
                <Select
                  allowClear
                  placeholder="Search content provider..."
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  onSearch={handleSearch}
                  onClear={handleClear}
                  loading={searchLoading}
                  notFoundContent={searchLoading ? 'Searching...' : 'No companies found'}
                  onSelect={value =>
                    handleFilterValueChange(prev => {
                      const currentIds = prev.content_provider_ids
                        ? prev.content_provider_ids.split(',').filter(Boolean)
                        : [];
                      const newIds = [...new Set([...currentIds, value])];
                      return {
                        ...prev,
                        content_provider_ids: newIds.join(','),
                      };
                    })
                  }
                  onDeselect={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      content_provider_ids: prev.content_provider_ids
                        .split(',')
                        .filter(Boolean)
                        .filter(id => id !== value)
                        .join(','),
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleDropdownVisibleChange}
                >
                  {searchCompanies.map(renderCompanyOption)}
                </Select>
              </Form.Item>

              <Form.Item name="season_ids" label="Season" style={{ width: '40%' }}>
                <Select
                  allowClear
                  mode="multiple"
                  showSearch
                  placeholder="Search season..."
                  filterOption={false}
                  onSearch={handleSeasonSearch}
                  onClear={handleSeasonClear}
                  loading={seasonSearchLoading}
                  notFoundContent={seasonSearchLoading ? 'Searching...' : 'No seasons found'}
                  onSelect={value =>
                    handleFilterValueChange(prev => {
                      const currentIds = prev.season_ids
                        ? prev.season_ids.split(',').filter(Boolean)
                        : [];
                      const newIds = [...new Set([...currentIds, value])];
                      return {
                        ...prev,
                        season_ids: newIds.join(','),
                      };
                    })
                  }
                  onDeselect={value =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      season_ids: prev.season_ids
                        .split(',')
                        .filter(Boolean)
                        .filter(id => id !== value)
                        .join(','),
                    }))
                  }
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  onDropdownVisibleChange={handleSeasonDropdownVisibleChange}
                >
                  {searchSeasons.map(renderSeasonOption)}
                </Select>
              </Form.Item>

              <Form.Item name="group_by" label="Group By">
                <Radio.Group
                  style={{ marginBottom: 20, fontSize: 16 }}
                  onChange={e =>
                    handleFilterValueChange(prev => ({
                      ...prev,
                      group_by: e.target.value,
                    }))
                  }
                >
                  Group By:
                  <Radio style={{ marginLeft: 10 }} value="yearmon">
                    Year Month
                  </Radio>
                  <Radio style={{ marginLeft: 10 }} value="yearmon_content_provider">
                    Year Month Content Provider
                  </Radio>
                  <Radio style={{ marginLeft: 10 }} value="yearmon_season">
                    Year Month Season
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Apply Filters
                  </Button>
                  <Button onClick={handleFilterReset}>Reset</Button>
                </Space>
              </Form.Item>
            </Form>
          ),
        },
      ]}
    />
  );
};
